import React, { useState, useEffect, useCallback } from "react";
import { Paginator } from "primereact/paginator";
import { useLocation, useNavigate } from "react-router-dom";

import { Device } from "types/device.types";
import { useDevices } from "hooks/useDevices";
import { useDevicesListActions } from "hooks/useDevicesListActions";
import { useTemporaryDealerAccessRequests } from "hooks/useTemporaryDealerAccess";
import { useAuth } from "context/AuthContext";

import DevicesCardView from "components/devices/DevicesCardView";
import DevicesTableView from "components/devices/DevicesTableView";
import DevicesConfirmDialog from "components/devices/DevicesConfirmDialog";
import { DevicesListToolbar } from "components/devices/DevicesListToolbar";
import { DevicesMapPanel } from "components/devices/DevicesMapPanel";
import { TemporaryAccessRequestDialog } from "components/devices/TemporaryAccessRequestDialog";
import AddDeviceWizard from "components/AddDeviceWizard";
import DeviceConfirmationDialog from "components/device/DeviceConfirmationDialog";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";
import {
  DevicesViewType,
  readDevicesViewPreference,
  writeDevicesViewPreference,
} from "utils/devicesViewPreference";
import { EmptyState } from "components/shared/EmptyState";
import { PageError } from "components/shared/PageError";
import { searchEmpty, zeroData } from "utils/emptyCopy";
import { sortDevicesByAlarm } from "utils/sortDevicesByAlarm";

import {
  PageContainer,
  PageTitle,
  PaginationContainer,
} from "styles/devices/devicesList.styles";

interface DevicesListPageProps {
  isAdminView?: boolean;
}

const DevicesListPage: React.FC<DevicesListPageProps> = ({
  isAdminView = false,
}) => {
  const [viewType, setViewType] = useState<DevicesViewType>(
    readDevicesViewPreference,
  );
  const [selectedMapDevice, setSelectedMapDevice] = useState<Device | null>(
    null,
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [temporaryAccessDialogVisible, setTemporaryAccessDialogVisible] =
    useState(false);
  const { user } = useAuth();
  const canAddDevice = !isAdminView;
  const isDealer = !isAdminView && user?.role === "DEALER";

  const handleViewChange = useCallback((view: DevicesViewType) => {
    setViewType(view);
    writeDevicesViewPreference(view);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const serialNumber = queryParams.get("serialNumber");
  const linkIdParam = queryParams.get("linkId");
  const linkId =
    linkIdParam && linkIdParam !== "undefined" && linkIdParam !== "null"
      ? linkIdParam
      : undefined;

  const refetchRef = React.useRef<() => void>(() => undefined);
  const actions = useDevicesListActions({
    refetch: () => refetchRef.current(),
  });
  const {
    data: temporaryRequests = [],
    isLoading: isLoadingTemporaryRequests,
  } = useTemporaryDealerAccessRequests(user?.id, isDealer);
  const openTemporaryAccessRequestCount = isDealer
    ? temporaryRequests.filter(
        (request) =>
          request.status === "pending" || request.status === "active",
      ).length
    : 0;
  const temporaryRequestLimitReached =
    openTemporaryAccessRequestCount >= 10;

  const { data, isLoading, error, refetch } = useDevices(
    actions.currentPage,
    actions.pageSize,
    actions.sortField,
    actions.sortOrder,
    actions.searchValue,
    isAdminView,
    actions.locationSearch.active && actions.locationSearch.latitude !== null
      ? actions.locationSearch.latitude
      : undefined,
    actions.locationSearch.active && actions.locationSearch.longitude !== null
      ? actions.locationSearch.longitude
      : undefined,
    actions.locationSearch.active
      ? actions.locationSearch.radiusKm
      : undefined,
  );

  refetchRef.current = refetch;

  useEffect(() => {
    if (data?.pagination) setTotalRecords(data.pagination.totalItems);
  }, [data]);

  useEffect(() => {
    if (!serialNumber) return;
    navigate(
      buildDeviceDetailsPath({ serialNumber, linkId, isAdminView }),
      { replace: true },
    );
  }, [serialNumber, linkId, isAdminView, navigate]);

  if (error) {
    return (
      <PageContainer>
        <PageTitle>Devices</PageTitle>
        <PageError
          title="Error loading devices"
          message="Error loading devices. Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const devices = data?.response || [];
  // Alarm data comes from the IoT shadow, so the API can't sort by it.
  // Cards and the map list float errors/warnings to the top of the current
  // page; the table keeps the column sort the user picked.
  const devicesByAlarm = sortDevicesByAlarm(devices);
  const pendingTemporaryAccessRequests = isDealer
    ? temporaryRequests.filter((request) => request.status === "pending")
    : [];
  const isLoadingPendingTemporaryAccessCards =
    isDealer && isLoadingTemporaryRequests && devices.length === 0;
  const hasPendingTemporaryAccessCards =
    pendingTemporaryAccessRequests.length > 0;
  const pageTitle = isAdminView ? "All Devices (Admin View)" : "My Devices";
  const showEmpty =
    !isLoading &&
    devices.length === 0 &&
    viewType !== "map" &&
    !(
      viewType === "cards" &&
      (isLoadingPendingTemporaryAccessCards || hasPendingTemporaryAccessCards)
    );
  const emptyMessage = actions.searchValue
    ? searchEmpty("devices")
    : canAddDevice
      ? `${zeroData("devices")} Try adjusting your search criteria or add a new device.`
      : `${zeroData("devices")} Try adjusting your search criteria.`;

  return (
    <PageContainer>
      <PageTitle>{pageTitle}</PageTitle>

      <DevicesListToolbar
        searchInputValue={actions.searchInputValue}
        onSearchChange={actions.handleSearchChange}
        viewType={viewType}
        onViewChange={handleViewChange}
        onAddDeviceClick={
          canAddDevice ? actions.handleAddDeviceClick : undefined
        }
        onRequestTemporaryAccessClick={
          isDealer
            ? () => setTemporaryAccessDialogVisible(true)
            : undefined
        }
        temporaryRequestLimitReached={temporaryRequestLimitReached}
      />

      <DevicesConfirmDialog
        visible={actions.updateManager.confirmDialogVisible}
        onHide={actions.updateManager.hideConfirmDialog}
        onConfirm={actions.updateManager.executeUpdate}
        isLoading={actions.updateManager.isUpdating}
        type={actions.updateManager.updateType}
        deviceCount={actions.updateManager.selectedDevices.length}
      />

      <DeviceConfirmationDialog
        visible={actions.deleteConfirmVisible}
        title="Remove device access"
        message="This removes this Lifeguard from your dealer account. It does not delete the Lifeguard or customer data."
        icon={{
          className: "pi pi-exclamation-triangle",
          color: "var(--red-500)",
        }}
        onHide={actions.hideDeleteConfirm}
        onConfirm={actions.executeDelete}
        isProcessing={actions.removeDeviceMutation.isPending}
        confirmButtonLabel="Remove access"
        cancelButtonLabel="Keep access"
        confirmButtonClassName="p-button-danger"
      />

      {showEmpty ? (
        <EmptyState
          message={emptyMessage}
          actionLabel={canAddDevice ? "Add Device" : undefined}
          onAction={canAddDevice ? actions.handleAddDeviceClick : undefined}
        />
      ) : (
        <div>
          {viewType === "table" ? (
            <DevicesTableView
              devices={devices}
              isLoading={isLoading}
              isAdminView={isAdminView}
              selectedDevices={actions.updateManager.selectedDevices}
              onSelectionChange={actions.updateManager.setSelectedDevices}
              onFirmwareUpdateClick={
                actions.updateManager.openFirmwareUpdateDialog
              }
              onNipperUpdateClick={actions.updateManager.openNipperUpdateDialog}
              isUpdating={actions.updateManager.isUpdating}
              onSort={actions.onSortChange}
              sortField={actions.sortField}
              sortOrder={actions.sortOrder}
            />
          ) : viewType === "cards" ? (
            <DevicesCardView
              devices={devicesByAlarm}
              isLoading={isLoading || isLoadingPendingTemporaryAccessCards}
              isAdminView={isAdminView}
              pendingTemporaryAccessRequests={pendingTemporaryAccessRequests}
              onDeleteClick={actions.handleDeleteDevice}
              onAliasUpdated={actions.handleAliasUpdated}
            />
          ) : (
            <DevicesMapPanel
              devices={devicesByAlarm}
              isLoading={isLoading}
              selectedDevice={selectedMapDevice}
              onDeviceSelect={setSelectedMapDevice}
              emptyMessage={emptyMessage}
              isLocationSearchActive={actions.locationSearch.active}
              onLocationSearch={actions.handleLocationSearchAndRefetch}
              onClearLocationSearch={
                actions.handleClearLocationSearchAndRefetch
              }
              canAddDevice={canAddDevice}
              onAddDeviceClick={actions.handleAddDeviceClick}
            />
          )}

          {data?.pagination && (
            <PaginationContainer>
              <Paginator
                first={(actions.currentPage - 1) * actions.pageSize}
                rows={actions.pageSize}
                totalRecords={totalRecords}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={actions.onPageChange}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              />
            </PaginationContainer>
          )}
        </div>
      )}

      {canAddDevice && (
        <AddDeviceWizard
          visible={actions.wizardVisible}
          onHide={actions.handleWizardHide}
          onComplete={actions.handleWizardComplete}
        />
      )}
      {isDealer && (
        <TemporaryAccessRequestDialog
          visible={temporaryAccessDialogVisible}
          onHide={() => setTemporaryAccessDialogVisible(false)}
          requestLimitReached={temporaryRequestLimitReached}
        />
      )}
    </PageContainer>
  );
};

export default DevicesListPage;
